import { UserWorkspaceRole } from "@common/userWorkspaceRoles";
import { useOverlays } from "contexts/OverlayContext";
import { PlainModal } from "@design-system/Modal/Modal";
import { useInviteMemberWithOrganizationCheck } from "./useInviteMemberWithOrganizationCheck";
import { useMemberManagementDefaults } from "./useMemberManagementDefaults";
import {
    AddMemberOptions,
    MemberManagementOverlayEntity,
} from "./memberManagement.types";
import { AddMemberModal } from "./AddMemberModal";

interface MemberManagementAddMemberOverlayData {
    workspaceId?: string;
    actingUserRole?: UserWorkspaceRole;
    entities?: MemberManagementOverlayEntity[];
    addMemberHeader?: React.ReactNode;
    options?: AddMemberOptions;
}

interface MemberManagementAddMemberModalContentProps {
    overlayData: MemberManagementAddMemberOverlayData;
    closeOverlay: () => void;
    openOverlay: ReturnType<typeof useOverlays>["openOverlay"];
    overlayIsClosing: boolean;
}

const MemberManagementAddMemberModalContent = ({
    overlayData,
    closeOverlay,
    openOverlay,
    overlayIsClosing,
}: MemberManagementAddMemberModalContentProps) => {
    const { workspaceId, actingUserRole, entities } =
        useMemberManagementDefaults(overlayData);

    const { addMemberHeader, options } = overlayData;
    const handleClose = () => {
        closeOverlay();
        options?.onClose?.();
    };
    const { handleSubmit } = useInviteMemberWithOrganizationCheck({
        workspaceId,
        onInvited: options?.onInvited,
        onAfterDirectInvite: handleClose,
        onOrganizationsFound: ({ organizations, confirmInvite }) => {
            openOverlay("memberManagementConfirmOrganizations", {
                organizations,
                onBackToAddMember: overlayData,
                onConfirm: confirmInvite,
                onClose: options?.onClose,
            });
        },
    });

    if (!workspaceId || !actingUserRole) {
        return null;
    }

    return (
        <PlainModal show={!overlayIsClosing} onHide={handleClose}>
            <AddMemberModal
                header={addMemberHeader}
                actingUserRole={actingUserRole}
                entities={entities}
                closeModal={handleClose}
                providedEmail={options?.email}
                providedRole={options?.role}
                submitHandler={handleSubmit}
                initialEntityIds={options?.initialEntityIds}
            />
        </PlainModal>
    );
};

export const MemberManagementAddMemberModalOverlay = () => {
    const { getOverlayInfo, openOverlay, closeOverlay, overlayIsClosing } =
        useOverlays();
    const overlayInfo = getOverlayInfo("memberManagementAddMember");

    if (!overlayInfo?.data) {
        return null;
    }

    return (
        <MemberManagementAddMemberModalContent
            overlayData={overlayInfo.data}
            closeOverlay={closeOverlay}
            openOverlay={openOverlay}
            overlayIsClosing={overlayIsClosing}
        />
    );
};
